import { ComponentType, Suspense, useLayoutEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import type { MegaColumn, MegaComponentColumn, MegaComponentType, MegaMenuConfig } from '@repo/types';
import { cmsWebComponentsMap } from '@ui/webComponentExports';
// Relative import: the web app's Vite config has no generic @ui/cms/* alias.
import { CmsComponent } from '../../../cms/contracts';
import { ErrorBoundary } from '@ui/common/components/ErrorBoundary';
import { linkTargetProps, toHref } from './useActiveNav';
import type { MegaEmbedProps } from './megaEmbeds';
import { MEGA_EMBEDS } from './megaEmbeds';

// Configured columns render the real CMS component (same registry the page
// builder uses); unconfigured ones fall back to the compact embeds.
const MEGA_TO_CMS: Record<MegaComponentType, CmsComponent> = {
  leagueTable: CmsComponent.LeagueTable,
  fixtures: CmsComponent.FixtureList,
  results: CmsComponent.FixtureList,
  topScorers: CmsComponent.PlayersStatsTable,
};

export type MegaMenuProps = {
  config: MegaMenuConfig;
  /** The toggle button element of the open tab — used to center the panel. */
  anchorEl: HTMLElement | null;
  /** Close callback fired when a link inside the panel is clicked. */
  onNavigate: () => void;
};

const EDGE_GAP = 8;

function offsetWithin(el: HTMLElement, ancestor: HTMLElement): number {
  let x = 0;
  let node: HTMLElement | null = el;
  while (node && node !== ancestor) {
    x += node.offsetLeft;
    node = node.offsetParent as HTMLElement | null;
  }
  return x;
}

/**
 * White mega panel with up to 4 columns (titled links or live embeds),
 * centered under the open tab and clamped inside the header.
 */
export function MegaMenu({ config, anchorEl, onNavigate }: MegaMenuProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const [left, setLeft] = useState(0);

  useLayoutEffect(() => {
    const panel = panelRef.current;
    const header = anchorEl?.closest<HTMLElement>('.header') ?? null;
    if (!panel || !anchorEl || !header) {
      return;
    }

    const recompute = () => {
      const tabCenter = offsetWithin(anchorEl, header) + anchorEl.offsetWidth / 2;
      const panelWidth = panel.offsetWidth;
      const headerWidth = header.offsetWidth;
      setLeft(Math.max(EDGE_GAP, Math.min(tabCenter - panelWidth / 2, headerWidth - panelWidth - EDGE_GAP)));
    };

    recompute();
    const observer = new ResizeObserver(recompute);
    observer.observe(panel);
    window.addEventListener('resize', recompute);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', recompute);
    };
  }, [anchorEl]);

  return (
    <div className="nav__mega" ref={panelRef} style={{ left }}>
      {config.columns.map((column, index) => (
        <MegaColumnView key={index} column={column} onNavigate={onNavigate} />
      ))}
    </div>
  );
}

// Mega documents come from a Mixed Mongo field, so legacy or hand-edited data
// can carry component names outside the typed union — widen the lookup so the
// runtime guard below stays live under the type contract.
const EMBED_LOOKUP = MEGA_EMBEDS as Partial<Record<string, (props: MegaEmbedProps) => JSX.Element | null>>;
const CMS_LOOKUP = MEGA_TO_CMS as Partial<Record<string, CmsComponent>>;

/**
 * A column configured with full CMS form data — renders the real CMS
 * component. Suspense covers the lazily-loaded registry entries and the
 * ErrorBoundary keeps a bad config from taking the whole panel down.
 */
function CmsComponentColumn({ column }: { column: MegaComponentColumn }) {
  const cmsType = CMS_LOOKUP[column.component];
  // The registry value is a union of every component's props, which collapses
  // to `never` when spread — widen it like the page builder's Row does.
  const Component = cmsType
    ? (cmsWebComponentsMap[cmsType] as ComponentType<Record<string, unknown>> | undefined)
    : undefined;
  if (!Component) {
    return null;
  }
  return (
    <div className="nav__mega-col nav__mega-col--comp nav__mega-col--cms">
      <ErrorBoundary fallback={null}>
        <Suspense fallback={null}>
          <Component {...(column.config as Record<string, unknown>)} />
        </Suspense>
      </ErrorBoundary>
    </div>
  );
}

function MegaColumnView({ column, onNavigate }: { column: MegaColumn; onNavigate: () => void }) {
  if (column.type === 'component') {
    if (column.config) {
      return <CmsComponentColumn column={column} />;
    }
    const Embed = EMBED_LOOKUP[column.component];
    if (!Embed) {
      return null;
    }
    return (
      <div className="nav__mega-col nav__mega-col--comp">
        <Embed competitionId={column.competitionId} onNavigate={onNavigate} />
      </div>
    );
  }

  const links = column.links.filter((link) => link.visible !== false);
  return (
    <div className="nav__mega-col">
      <h6 className="nav__mega-title">{column.title}</h6>
      {links.map((link) =>
        link.url ? (
          <Link
            key={link.id}
            className="nav__mega-link small-text"
            to={toHref(link.url)}
            {...linkTargetProps(link.sameTab)}
            onClick={onNavigate}
          >
            {link.label}
          </Link>
        ) : (
          <span key={link.id} className="nav__mega-link small-text">
            {link.label}
          </span>
        )
      )}
    </div>
  );
}
